

import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import styles from "./Form.module.css";
import { mapValues } from "@/utils";
import Tooltip from "../Tooltip";

export type Validator<T extends Record<string, any>, U extends keyof T> = {
  (value: T[U]): string | null | undefined;
}

export type FieldFlags<T extends Record<string, any>> = {
  [U in keyof T]: { valid?: boolean, invalid?: boolean, touched?: boolean, error?: string };
};

type WrappedValidator<T> = {
  (always?: boolean, form?: FormState<T>): string | null | undefined;
};

type Validators<T extends Record<string, any>> = {
  [U in keyof T]?: WrappedValidator<T>;
};

export interface FormState<T> {
  value: T;
  state: FieldFlags<T>;
  validate: (options?: { quiet?: boolean }) => boolean;
  setValue: (value: T, validate?: boolean) => void;
  setFieldValue: <U extends keyof T>(name: U, fieldValue: T[U]) => void;
  resetState: () => void;
  __internal: {
    setValidator: <U extends keyof T>(name: U, validator: WrappedValidator<T>) => void;
    setError: <U extends keyof T>(form: FormState<T>, name: U, error: string | null | undefined, quiet: boolean) => void;
    setTouched: <U extends keyof T>(form: FormState<T>, name: U) => void;
  };
}

export function useForm<T extends Record<string, any>>(initialValue: T | (() => T)): FormState<T> {
  const [value, setValue] = useState<T>(initialValue);
  const [validators] = useState<Validators<T>>({});
  const [state, setState] = useState<FieldFlags<T>>(mapValues(value, () => ({})));

  const setFieldValue = function <U extends keyof T>(name: U, fieldValue: T[U]) {
    if (value[name] === fieldValue) return;
    this.value = { ...this.value, [name]: fieldValue };
    setValue(this.value);
  };
  const setValidator = function <U extends keyof T>(name: U, validator: WrappedValidator<T>) {
    validators[name] = validator;
  };
  const setError = function <U extends keyof T>(form: FormState<T>, name: U, error: string | null | undefined, quiet: boolean) {
    if (form.state[name].error === error) return;
    form.state = {
      ...form.state,
      [name]: {
        touched: form.state[name].touched || !quiet,
        error,
        valid: error == null,
        invalid: error != null
      }
    };
    setState(form.state);
  };
  const setTouched = function <U extends keyof T>(form: FormState<T>, name: U) {
    form.state = { ...form.state, [name]: { ...form.state[name], touched: true } };
    setState(form.state);
  };
  const resetState = () => setState(mapValues(value, () => ({})));

  const form = {
    value,
    state,
    resetState,
    setFieldValue,
    setValue(value: T, validate?: boolean) {
      this.value = value;
      setValue(this.value);
      if (validate) requestAnimationFrame(() => this.validate());
    },
    validate(options?: {quiet?: boolean}) {
      let valid = true;
      for (const validator of Object.values(validators)) {
        valid = validator(options?.quiet, this) == null && valid;
      }
      return valid;
    },
    __internal: {
      setValidator,
      setError,
      setTouched,
    },
  };

  form.validate = form.validate.bind(form);
  form.setValue = form.setValue.bind(form);
  form.setFieldValue = form.setFieldValue.bind(form);

  return form;
}

export interface FormFieldProps<T, U extends keyof T> extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  form: FormState<T>;
  name: U;
  children: React.ReactNode;
  label?: React.ReactNode;
  showLabel?: boolean;
  displayName?: string;
  required?: boolean;
  alignInvalid?: "left" | "right";
  validator?: Validator<T, U>;
}

export function FormField<T, U extends keyof T>({
  form,
  name,
  children,
  label,
  showLabel = label != null,
  displayName,
  required = true,
  validator: _validator,
  className,
  alignInvalid = "right",
  ...rest
}: FormFieldProps<T, U>) {
  const ref = useRef<HTMLDivElement>();
  const validator = useCallback((quiet?: boolean, localForm?: FormState<T>) => {
    localForm = localForm ?? form;
    let error;
    if (required && (localForm.value[name] === "" || localForm.value[name] == null)) {
      error = `${displayName ?? "This field"} is required`;
    } else {
      error = _validator?.(localForm.value[name]);
    }
    if (!quiet || form.state[name].touched) {
      localForm.__internal.setError(localForm, name, error, quiet);
    }
    return error;
  }, [form, name, displayName, required, _validator]);

  useEffect(() => {
    if (!ref.current) return;
    const onChange = () => {
      form.__internal.setTouched(form, name);
      validator();
    }
    ref.current.addEventListener("change", onChange, true);
    ref.current.addEventListener("blur", onChange, true);
    return () => {
      ref.current?.removeEventListener("change", onChange, true);
      ref.current?.removeEventListener("blur", onChange, true);
    };
  }, [ref.current, form]);

  useEffect(() => {
    form.__internal.setValidator(name, validator);
    validator(true);
  }, [form.value[name], validator]);

  return (
    <div className={`${styles.field} ${className ?? ""}`} ref={ref} {...rest}>
      {showLabel && <label className="strong" style={{paddingBottom: "4px"}}>{label ?? `${displayName ?? "Field"}:`}</label>}
      {children}
      <Tooltip title={form.state[name].error}><span className={`${styles.invalidInputText} ${styles[alignInvalid]}`}>{form.state[name].error}</span></Tooltip>
    </div>
  );
}
